import { flairSpecs } from '@gardener/assets/flair';
import { type ItemKind, SETS, type SetKey } from '@gardener/content';
import { type AdminSkin, type AdminSkinsView, MAX_SKIN_PRICE } from '@gardener/protocol';
import { createEffect, createMemo, createResource, createSignal, For, on, onCleanup, Show } from 'solid-js';
import { call } from './api';
import { go, route } from './nav';
import { drawTile, Showcase, type View, wornFor } from './showcase';
import {
  ago,
  ask,
  count,
  EmptyState,
  Icon,
  IconButton,
  LoadingLine,
  optimistic,
  SETTINGS_INPUT,
  Segmented,
  SettingsCard,
  SettingsItem,
  Switch,
  toast,
  Well,
} from './ui';

const STYLE: Record<AdminSkin['style'], string> = {
  finish: 'Finish',
  effect: 'Effect',
  polished: 'Polished',
  head: 'Head',
  outfit: 'Outfit',
};
const KINDS: readonly (readonly [ItemKind, string])[] = [
  ['head', 'Heads'],
  ['outfit', 'Outfits'],
  ['gun', 'Gun skins'],
];

/** What a skin is on a gardener (the showcase's note). */
const NOTE: Record<AdminSkin['style'], string> = {
  finish: 'A finish paints every gun; this is the default one.',
  effect: 'It redraws the default gun, its shots and their sounds; crate guns stay stock.',
  polished: 'The default gun drawn smooth, its owner’s kills on the counter; every other gun takes its colours.',
  head: 'A head: a hat and a face together, worn over any outfit.',
  outfit: 'An outfit: what a gardener wears over the team’s trousers, under any head.',
};
/** A mythic piece's (ADR-0160), after its note. */
const FLAIR = ' Mythic: it moves on its own (its flair), drawn here by the game’s own engine.';

/** Where skin `s` is: in the Store, in preview (released or not), or not sold. */
const where = (s: AdminSkin) =>
  s.rejected
    ? 'turned down'
    : s.preview
      ? s.live
        ? 'in preview, released'
        : 'in preview'
      : s.live
        ? 'in the Store'
        : 'not sold';

/** A set's name (`SETS`; a season's or the Creator Program's set isn't there: its key). */
const setName = (key: string) => (Object.hasOwn(SETS, key) ? SETS[key as SetKey].name : key);

/** How the stage shows a skin until its admin picks: a gardener's piece in the lobby, a gun in a fight. */
const [chosen, setChosen] = createSignal<View | null>(null);
/** A set's piece is shown in its whole set until its admin says otherwise. */
const [whole, setWhole] = createSignal(true);

/**
 * Skins (ADR-0154; a gardener's too, ADR-0160): those in preview are in the game's build but nobody's; one released here
 * is in every player's Store at the price set (their next visit to it), and pulled again it is sold no more (whoever
 * bought it keeps it). Each is shown as the Store's tile shows it and, picked, on a gardener (ADR-0157): in a fight or
 * big in the lobby's spotlight, drawn here from the game's art.
 */
export function Skins() {
  const [list, { mutate, refetch }] = createResource(() => call<AdminSkinsView>('GET', '/api/admin/skins'));
  // An errored resource throws when read: its error first.
  const skins = () => (list.error ? [] : (list()?.skins ?? []));
  const preview = () => skins().filter((s) => s.preview);
  const rest = () => skins().filter((s) => !s.preview);
  /** Those in preview by the set each is a piece of ('': none), in the list's order. */
  const sets = createMemo(() => {
    const by = new Map<string, AdminSkin[]>();
    for (const s of preview()) by.set(s.set ?? '', [...(by.get(s.set ?? '') ?? []), s]);
    return [...by];
  });
  /** The skin shown: the address's (a gun skin's bare key, as links were before ADR-0160, too), else the first in preview. */
  const picked = createMemo(() => {
    const arg = route().arg;
    return skins().find((s) => s.id === arg || s.id === `gun:${arg}`) ?? preview()[0] ?? skins()[0];
  });
  let stage: HTMLDivElement | undefined;
  // One picked far down the list on a phone: its stage is over the list, so it comes into view.
  createEffect(
    on(
      () => route().arg,
      (arg) => arg && stage?.scrollIntoView({ block: 'nearest' }),
      { defer: true },
    ),
  );

  /** Skin `id` as `change` makes it in the list; returns how to take it back. */
  const edit = (id: string, change: (s: AdminSkin) => AdminSkin) => {
    const before = list();
    if (before) mutate({ skins: before.skins.map((s) => (s.id === id ? change(s) : s)) });
    return () => mutate(before);
  };
  /** Sends skin `s` released or not at `price`, shown at once. */
  const send = (s: AdminSkin, live: boolean, price: number, said: string) =>
    optimistic(
      () => edit(s.id, (x) => ({ ...x, live, price })),
      () => call<AdminSkin>('PUT', `/api/admin/skins/${encodeURIComponent(s.id)}`, { live, price }),
      (after) => {
        edit(s.id, () => after);
        toast(said);
      },
    );

  async function release(s: AdminSkin, live: boolean) {
    if (live && s.price === null) return toast('Set its price first', 'error');
    const price = s.price as number;
    const sure = await ask(
      live
        ? {
            title: `Release ${s.name} for ${count(price)} seeds?`,
            body: `It is in every player’s Store from their next visit to it. Partners have it at once.${
              s.set ? ` The ${setName(s.set)} set is sold whole once all three of its pieces are released.` : ''
            }`,
            confirm: 'Release',
          }
        : {
            title: `Pull ${s.name} from the Store?`,
            body: 'Nobody can buy it any more. Whoever bought it keeps it.',
            confirm: 'Pull it',
            danger: true,
          },
    );
    if (sure !== null) await send(s, live, price, live ? `${s.name} is in the Store` : `${s.name} is pulled`);
  }
  /** A new price for `s` (asked first while it is sold): false when it was refused or not sent. */
  async function price(s: AdminSkin, price: number): Promise<boolean> {
    if (!Number.isInteger(price) || price < 1 || price > MAX_SKIN_PRICE) {
      toast(`A price is a whole number of seeds, 1 to ${count(MAX_SKIN_PRICE)}`, 'error');
      return false;
    }
    if (s.live) {
      const sure = await ask({
        title: `Sell ${s.name} for ${count(price)} seeds?`,
        body: `It costs ${count(s.price ?? 0)} now. Players see the new price from their next visit to the Store.`,
        confirm: 'Change the price',
      });
      if (sure === null) return false;
    }
    return send(s, s.live, price, `${s.name}: ${count(price)} seeds`);
  }
  /** Turns `s` down, its whole set with it (ADR-0166): for good, so asked first. */
  async function reject(s: AdminSkin) {
    const what = s.set ? `the ${setName(s.set)} set` : s.name;
    const sure = await ask({
      title: `Take ${what} out of the game?`,
      body: 'It can never be released after this, and the daily skins routine deletes it from the game on its next run, then designs another in its place. There is no undo.',
      confirm: 'Don’t want it',
      danger: true,
    });
    if (sure === null) return;
    const goes = (x: AdminSkin) => x.preview && (s.set ? x.set === s.set : x.id === s.id);
    await optimistic(
      () => {
        const before = list();
        if (before) mutate({ skins: before.skins.map((x) => (goes(x) ? { ...x, rejected: true } : x)) });
        return () => mutate(before);
      },
      () => call<AdminSkinsView>('DELETE', `/api/admin/skins/${encodeURIComponent(s.id)}`),
      (after) => {
        for (const a of after.skins) edit(a.id, () => a);
        toast(`Turned down: ${what}`);
      },
    );
  }

  /** logdash's `DomainsHome` grid of tiles. */
  const grid = (label: string, of: AdminSkin[]) => (
    <ul class="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-2" aria-label={label}>
      <For each={of}>{(s) => <SkinTile s={s} picked={picked()?.id === s.id} />}</For>
    </ul>
  );
  /** A section's title and what it is (logdash's settings-card header), over its runs of tiles, or `empty` in a well. */
  const section = (title: string, description: string, has: boolean, empty: string, runs: () => unknown) => (
    <>
      <div class="flex min-w-0 flex-col gap-0.5 px-5 pt-3 pb-1 not-first:pt-5">
        <h2 class="text-[13px] font-medium">{title}</h2>
        <p class="text-fg-muted text-[13px]">{description}</p>
      </div>
      <Show
        when={has}
        fallback={
          <Well label={title}>
            <EmptyState centered title={empty} />
          </Well>
        }
      >
        {runs() as never}
      </Show>
    </>
  );
  /** A run's heading inside a section: a set in preview, a kind of skin. */
  const run = (title: string, aside?: string) => (
    <h3 class="flex items-baseline gap-2.5 px-5 pt-2 text-[13px] font-medium text-fg-secondary">
      {title}
      <Show when={aside}>
        <span class="text-fg-muted font-normal">{aside}</span>
      </Show>
    </h3>
  );

  return (
    <div class="@container flex w-full flex-col">
      <Show
        when={skins().length}
        fallback={
          <div class="p-2">
            <Well label="Skins">
              <Show
                when={list.error}
                fallback={
                  <Show when={list.loading} fallback={<EmptyState centered title="No skins" />}>
                    <div class="px-3 py-2.5">
                      <LoadingLine label="Loading skins" />
                    </div>
                  </Show>
                }
              >
                {(err) => (
                  <EmptyState centered title="Could not load the skins" description={(err() as Error).message}>
                    <button type="button" class="ld-button" data-size="sm" onClick={() => void refetch()}>
                      Try again
                    </button>
                  </EmptyState>
                )}
              </Show>
            </Well>
          </div>
        }
      >
        <div class="grid items-start gap-2 p-2 @4xl:grid-cols-[minmax(0,1fr)_26rem]">
          <div class="flex min-w-0 flex-col gap-2">
            {section(
              'In preview',
              'In the game, not in the Store. Pick one, set a price, then release it.',
              preview().length > 0,
              'No skin is in preview',
              () => (
                <For each={sets()}>
                  {([set, pieces]) => (
                    <>
                      <Show when={set}>
                        {run(
                          `${setName(set)} set`,
                          pieces.some((s) => s.rejected)
                            ? 'turned down'
                            : `${pieces.filter((s) => s.live).length} of ${pieces.length} released`,
                        )}
                      </Show>
                      {grid(set ? `${setName(set)} set` : 'In preview', pieces)}
                    </>
                  )}
                </For>
              ),
            )}
            {section('Everything else', 'Priced in the game’s code.', rest().length > 0, 'No other skin', () => (
              <For each={KINDS}>
                {([kind, title]) => (
                  <Show when={rest().some((s) => s.kind === kind)}>
                    {run(title)}
                    {grid(
                      title,
                      rest().filter((s) => s.kind === kind),
                    )}
                  </Show>
                )}
              </For>
            ))}
          </div>
          <div ref={stage} class="min-w-0 @max-4xl:order-first @4xl:sticky @4xl:top-2">
            <Show when={picked()}>{(s) => <SkinStage s={s()} release={release} price={price} reject={reject} />}</Show>
          </div>
        </div>
      </Show>
    </div>
  );
}

/**
 * A skin as logdash's `DomainCard`, made short (dozens of them in a grid): its name and status over its price, then the
 * default gun in it, a head or an outfit, on the game's paper (the Store's tiles draw them on that); a click shows it.
 */
function SkinTile(props: { s: AdminSkin; picked: boolean }) {
  const s = () => props.s;
  return (
    <li class="flex min-w-0">
      <button
        type="button"
        class="focus-visible:outline-brand flex w-full min-w-0 cursor-pointer flex-col gap-2 rounded-2xl p-2 text-left focus-visible:outline-2 focus-visible:-outline-offset-2"
        classList={{
          'bg-surface-25-selected-bg': props.picked,
          'bg-surface-25-bg hover:bg-surface-25-hover-bg': !props.picked,
        }}
        aria-pressed={props.picked}
        title={`${s().name} · ${STYLE[s().style]}`}
        onClick={() => go('skins', s().id)}
      >
        <div class="flex min-w-0 flex-col gap-0.5 px-3 pt-1.5">
          <div class="flex min-w-0 items-center gap-2.5">
            <span class="min-w-0 truncate text-[13px] font-medium">{s().name}</span>
            {/* Its section says it is in preview: only a released one says so. */}
            <Show when={s().preview && s().live}>
              <span class="text-fg-tertiary ml-auto flex shrink-0 items-center gap-1.5 text-xs">
                <span class="bg-success size-1.5 rounded-full" />
                Live
              </span>
            </Show>
          </div>
          <span class="text-fg-muted truncate text-[13px] tabular-nums">
            {s().rejected
              ? 'Turned down'
              : s().price === null
                ? s().preview
                  ? 'No price yet'
                  : 'Not sold'
                : `${count(s().price ?? 0)} seeds`}
          </span>
        </div>
        <span class="flex h-27 items-center justify-center rounded-lg bg-[#e6d4b0]">
          <canvas class="h-24 w-32" ref={(c) => drawTile(c, props.s.kind, props.s.key)} />
        </span>
      </button>
    </li>
  );
}

/**
 * The skin picked on a gardener (`Showcase`, drawn here from the game's art), two ways (ADR-0160): in a fight, with
 * sound on a click, or big in the lobby's spotlight as PLAY shows you (a drag turns it); a set's piece in its whole set
 * or alone. One in preview with its price (saved on Enter or leaving the field), its release switch and the key that
 * turns it down (ADR-0166); one turned down says so, and nothing else. logdash's `SettingsCard`.
 */
function SkinStage(props: {
  s: AdminSkin;
  release: (s: AdminSkin, live: boolean) => void;
  price: (s: AdminSkin, price: number) => Promise<boolean>;
  reject: (s: AdminSkin) => void;
}) {
  const s = () => props.s;
  const [sound, setSound] = createSignal(false);
  const view = (): View => chosen() ?? (s().kind === 'gun' ? 'action' : 'lobby');
  const worn = createMemo(() => wornFor(s().kind, s().key, whole() && s().set !== null));
  /** Whether anything it is shown in has flair of its own (ADR-0160). */
  const mythic = () => flairSpecs(worn().hat, worn().look.outfit, worn().gun).length > 0;
  let show: Showcase | undefined;
  createEffect(() => show?.show(worn()));
  createEffect(() => {
    if (show) show.view = view();
  });
  onCleanup(() => show?.destroy());
  return (
    <SettingsCard
      title={s().name}
      description={
        <>
          {STYLE[s().style]}
          <Show when={s().set}>{(set) => <> · {setName(set())} set</>}</Show> · {where(s())}
          <Show when={s().at}>{(at) => <> · changed {ago(at())}</>}</Show>
        </>
      }
      actions={
        <Show when={view() === 'action'}>
          <IconButton
            well
            label={sound() ? 'Mute' : 'Sound'}
            onClick={() => {
              setSound(!sound());
              // Here, in the click: the page may start its audio only from one.
              if (show) show.sound = sound();
            }}
          >
            <Icon name={sound() ? 'sound' : 'mute'} class="size-4" />
          </IconButton>
        </Show>
      }
    >
      <div class="pt-1 pb-2">
        <div class="relative aspect-[16/10] overflow-hidden rounded-xl bg-[#3e8948]">
          <canvas
            class="absolute inset-0 size-full"
            ref={(c) => {
              show = new Showcase(c);
              show.start();
            }}
            role="img"
            aria-label={
              view() === 'lobby'
                ? `${s().name} on a gardener in the lobby's spotlight`
                : `${s().name} on a gardener firing at a scarecrow`
            }
          />
          <div class="pointer-events-none absolute inset-x-2.5 top-2.5 flex items-center justify-between gap-2.5 [&>*]:pointer-events-auto">
            <Segmented
              size="xs"
              value={view()}
              options={[
                ['action', 'In action'],
                ['lobby', 'Lobby'],
              ]}
              onChange={setChosen}
            />
            <Show when={s().set}>
              {(set) => (
                <label
                  class="bg-surface-50-bg text-fg-secondary flex h-7 cursor-pointer items-center gap-2 rounded-[9px] px-2.5 text-[13px] shadow-[inset_0_0_0_1px_var(--surface-50-border)]"
                  for="skin-whole"
                  title={`Shown in the whole ${setName(set())} set, or alone on the default gardener`}
                >
                  <Switch
                    id="skin-whole"
                    size="xs"
                    checked={whole()}
                    label={`The whole ${setName(set())} set`}
                    onChange={setWhole}
                  />
                  Whole set
                </label>
              )}
            </Show>
          </div>
        </div>
      </div>
      <Show when={s().rejected}>
        <SettingsItem>
          <p>Turned down</p>
          <p class="text-fg-muted">
            The daily skins routine deletes it from the game on its next run, and designs another in its place.
          </p>
        </SettingsItem>
      </Show>
      <Show when={s().preview && !s().rejected}>
        <SettingsItem>
          <div class="flex min-w-0 items-center gap-3">
            <span class="text-fg-muted w-16 shrink-0">Price</span>
            <PriceField s={s()} price={props.price} />
          </div>
        </SettingsItem>
        <SettingsItem
          action={
            <Switch
              id="skin-live"
              checked={s().live}
              disabled={s().price === null}
              label={`${s().name} in the Store`}
              onChange={(live) => props.release(s(), live)}
            />
          }
        >
          <label
            for="skin-live"
            class="block"
            classList={{ 'cursor-pointer': s().price !== null, 'cursor-not-allowed': s().price === null }}
            title={s().price === null ? 'Set a price first' : ''}
          >
            <p>In the Store</p>
            <p class="text-fg-muted">
              {s().live ? 'Players can buy it' : s().price === null ? 'Set a price first' : 'Not sold'}
            </p>
          </label>
        </SettingsItem>
        <SettingsItem
          action={
            <button
              type="button"
              class="ld-button"
              data-variant="danger"
              data-size="sm"
              onClick={() => props.reject(s())}
            >
              Don’t want it
            </button>
          }
        >
          <p>{s().set ? `Take the ${setName(s().set ?? '')} set out` : 'Take it out'}</p>
          <p class="text-fg-muted">
            {s().set ? 'All three pieces leave the game' : 'It leaves the game'} on the daily routine’s next run. Pull
            it first if it is sold.
          </p>
        </SettingsItem>
      </Show>
      <SettingsItem>
        <p class="truncate font-mono">{s().id}</p>
        <p class="text-fg-muted">
          {NOTE[s().style]}
          {mythic() ? FLAIR : ''}
        </p>
      </SettingsItem>
    </SettingsCard>
  );
}

/** A skin in preview's price, typed: saved on Enter or leaving the field, Escape puts it back. */
function PriceField(props: { s: AdminSkin; price: (s: AdminSkin, price: number) => Promise<boolean> }) {
  const s = () => props.s;
  /** Its price as the field shows it at rest: `40,000`. */
  const shown = () => (s().price === null ? '' : count(s().price ?? 0));
  const [typed, setTyped] = createSignal(shown());
  // The server's price back in the field once it answers (or a refusal took it back), and another skin's when picked.
  createEffect(on([() => s().id, () => s().price], () => setTyped(shown())));
  const commit = () => {
    const n = Number(typed().replace(/[\s,]/g, ''));
    if (typed().trim() === '' || n === s().price) return setTyped(shown());
    void props.price(s(), n).then((ok) => ok || setTyped(shown()));
  };
  return (
    <label class={`${SETTINGS_INPUT} -my-1.5 w-full max-w-64`} data-size="sm">
      <input
        class="tabular-nums"
        inputmode="numeric"
        aria-label={`${s().name}'s price`}
        placeholder="Not set"
        value={typed()}
        onInput={(e) => setTyped(e.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setTyped(shown());
            e.currentTarget.blur();
          }
        }}
      />
      <span class="text-fg-muted shrink-0">seeds</span>
    </label>
  );
}
